import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { AuthResponse, AuthTokens, User } from "@gb/contracts";
import { api, setAccessToken, setTokenRefresher } from "../api/client";

const TOKENS_KEY = "gb.tokens";
const USER_KEY = "gb.user";

type Status = "loading" | "authenticated" | "unauthenticated";

type AuthContextValue = {
  status: Status;
  user: User | null;
  accessToken: string | null;
  signIn: (res: AuthResponse) => void;
  signOut: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);

  // Restore a persisted session on first paint.
  useEffect(() => {
    try {
      const t = localStorage.getItem(TOKENS_KEY);
      const u = localStorage.getItem(USER_KEY);
      if (t && u) {
        const restored = JSON.parse(t) as AuthTokens;
        setTokens(restored);
        setUser(JSON.parse(u) as User);
        // Synchronously, before status flips: the first authenticated query
        // fires on the same paint as `authenticated`, and a token that arrived
        // an effect later would let it go out unauthenticated and 401.
        setAccessToken(restored.accessToken);
        setStatus("authenticated");
        return;
      }
    } catch {
      // fall through to unauthenticated
    }
    setStatus("unauthenticated");
  }, []);

  /**
   * The refresh token, off React state.
   *
   * The client can ask for a refresh at any moment, including from a query that
   * fired before this component re-rendered, so reading it from `tokens` would
   * hand back whatever value was captured in the closure that registered the
   * refresher. A ref is always current.
   */
  const refreshTokenRef = useRef<string | null>(null);
  refreshTokenRef.current = tokens?.refreshToken ?? null;

  // Give the API client a way to recover from an expired access token. Without
  // it, every screen breaks 15 minutes after sign-in (JWT_ACCESS_TTL) and the
  // only cure is signing out and in again.
  useEffect(() => {
    setTokenRefresher(async () => {
      const refreshToken = refreshTokenRef.current;
      if (!refreshToken) return null;

      const res = await api.auth.refresh({ body: { refreshToken } }).catch(() => null);
      if (!res || res.status !== 200) {
        // The refresh token is spent, revoked or expired — the session is over.
        // Clearing here rather than leaving a dead session in place is what
        // turns "every request fails forever" into "you are signed out".
        setTokens(null);
        setUser(null);
        setAccessToken(null);
        setStatus("unauthenticated");
        localStorage.removeItem(TOKENS_KEY);
        localStorage.removeItem(USER_KEY);
        return null;
      }

      // Both tokens rotate, so the new refresh token must be persisted too —
      // keeping the old one would make the *next* refresh fail.
      const next = res.body.tokens;
      setTokens(next);
      setAccessToken(next.accessToken);
      localStorage.setItem(TOKENS_KEY, JSON.stringify(next));
      return next.accessToken;
    });

    return () => setTokenRefresher(null);
  }, []);

  const signIn = (res: AuthResponse): void => {
    setTokens(res.tokens);
    setUser(res.user);
    setAccessToken(res.tokens.accessToken);
    setStatus("authenticated");
    localStorage.setItem(TOKENS_KEY, JSON.stringify(res.tokens));
    localStorage.setItem(USER_KEY, JSON.stringify(res.user));
  };

  const signOut = (): void => {
    const refreshToken = tokens?.refreshToken;
    setTokens(null);
    setUser(null);
    setAccessToken(null);
    setStatus("unauthenticated");
    localStorage.removeItem(TOKENS_KEY);
    localStorage.removeItem(USER_KEY);
    // Best-effort server-side revoke; the local session is already cleared.
    if (refreshToken) {
      void api.auth.logout({ body: { refreshToken } }).catch(() => undefined);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        status,
        user,
        accessToken: tokens?.accessToken ?? null,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
